import { useTranslation } from 'react-i18next';

/**
 * Hook for managing language/locale in the application
 *
 * @returns {Object} Locale management utilities
 * @returns {string} currentLanguage - Current language code (e.g., 'en', 'de')
 * @returns {string[]} supportedLanguages - Array of supported language codes
 * @returns {Function} changeLanguage - Function to change the current language
 * @returns {boolean} isLoading - Whether i18n is currently loading
 *
 * @example
 * const { currentLanguage, changeLanguage, supportedLanguages } = useLocale();
 *
 * // Change language
 * changeLanguage('de');
 *
 * // Check current language
 * console.log(currentLanguage); // 'en'
 */
export function useLocale() {
  const { i18n } = useTranslation();

  const currentLanguage = i18n.language || 'en';
  const supportedLanguages = i18n.options?.supportedLngs?.filter(lng => lng !== 'cimode') || ['en', 'de'];

  /**
   * Change the current language
   * @param {string} languageCode - The language code to switch to (e.g., 'en', 'de')
   * @returns {Promise<void>}
   */
  const changeLanguage = async (languageCode) => {
    if (!supportedLanguages.includes(languageCode)) {
      console.warn(`Language "${languageCode}" is not supported. Supported languages:`, supportedLanguages);
      return;
    }

    try {
      await i18n.changeLanguage(languageCode);
      // Update HTML lang attribute for accessibility
      document.documentElement.lang = languageCode;
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  /**
   * Get the display name for a language code
   * @param {string} languageCode - The language code
   * @returns {string} Display name
   */
  const getLanguageDisplayName = (languageCode) => {
    const displayNames = {
      en: 'English',
      de: 'Deutsch',
      el: 'Ελληνικά',
      it: 'Italiano',
      pt: 'Português',
    };
    return displayNames[languageCode] || languageCode;
  };

  return {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
    getLanguageDisplayName,
    isLoading: !i18n.isInitialized,
  };
}
